import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { UserStatusCounts } from "../types/users-api.types";
import { formatCount } from "../utils/statusCounts";

export interface UsersNoRolesNoteProps {
  counts: UserStatusCounts | null;
}

/**
 * "N personas sin ningún rol asignado."
 *
 * **Deliberately not a sixth tile, and carrying no filter affordance.** `no_roles` is a separate `NOT EXISTS`
 * query that cuts ACROSS the status buckets instead of partitioning them, and it is not part of `all` — so a
 * tile beside the five would imply it belongs to the same partition. There is also no `has_roles` param, so a
 * clickable tile would filter nothing, or worse, filter the loaded page and imply it filtered the population.
 * Different kind of fact, different kind of element.
 *
 * `—` when the counts could not be read: a zero here would read as "everyone has a role", the opposite of
 * unknown.
 */
export function UsersNoRolesNote({ counts }: UsersNoRolesNoteProps) {
  const { t } = useTranslations("users");

  if (counts && counts.no_roles === 0) {
    return <p className="text-sm text-muted-foreground">{t("stat_no_roles_zero")}</p>;
  }

  const value = counts ? counts.no_roles : null;

  return (
    <p className="text-sm text-muted-foreground">
      <span
        className={cn(
          "font-geist-mono font-medium tabular-nums",
          value !== null && value > 0 ? "text-destructive" : "text-muted-foreground",
        )}
      >
        {formatCount(value)}
      </span>{" "}
      {t("stat_no_roles")}
    </p>
  );
}
